import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { sortBy } from 'lodash';
import { useId } from 'react';

import { FormField, api, getQueryProps } from 'common';

import { getFilterOptions } from '../../../../../../../components/FiltersV2/filterOptions';
import { useFlowDefinitionContext } from '../../../../context';
import { useReactFlowViewContext } from '../../../context/context';
import { useSelectedNodeField } from '../../ConfigFields/useSelectedNodeField';

interface PracticeAutocompleteProps {
  label: string;
  fieldName: string;
}

export function PracticeAutocomplete(props: PracticeAutocompleteProps) {
  const { label, fieldName } = props;
  const { field, value, setValue, visibleError } =
    useSelectedNodeField<api.FlowDefinition.StartBlock['config']['testPracticeId']>(fieldName);
  const { flowDefinition } = useFlowDefinitionContext();
  const { isEditable } = useReactFlowViewContext();

  const { data: practices = [], isLoading } = useQuery({
    ...getQueryProps(api.client.Practice, 'findAllBasic', {
      organizationId: flowDefinition.organizationId,
    }),
  });
  const id = useId();

  const getOptionLabel = (option: api.Practice.PracticeBasic) =>
    api.Practice.getInternalNameOrName(option);

  return (
    <FormField id={id} label={label} error={visibleError}>
      <Autocomplete
        id={id}
        loading={isLoading}
        onBlur={field.onBlur}
        value={
          value
            ? (practices.find(({ id }) => id === value) ??
              ({ id: value, name: value } as api.Practice.PracticeBasic))
            : null
        }
        multiple={false}
        disabled={!isEditable}
        onChange={(_, newPractice) => {
          setValue(newPractice?.id ?? undefined);
        }}
        filterOptions={(options, state) =>
          getFilterOptions(options, state.inputValue, getOptionLabel)
        }
        options={sortBy(practices, (option) => getOptionLabel(option).toUpperCase())}
        getOptionLabel={(option) => {
          if (Array.isArray(option)) {
            return option.map((o: api.Practice.PracticeBasic) => getOptionLabel(o)).join(', ');
          }

          return getOptionLabel(option);
        }}
        renderInput={(params) => <TextField {...params} size="small" autoFocus />}
        renderOption={(props, option) => (
          /**
           * `renderOption` is required to ensure that the filtered items have unique keys.
           * See this article: https://lockstep.io/blog/debugging-lesson-well-learned-fixing-material-ui-autocomplete-search/
           */
          <li {...props} key={option.id}>
            {getOptionLabel?.(option)}
          </li>
        )}
      />
    </FormField>
  );
}
